import type { Id } from "@convex/_generated/dataModel";
import {
  CreditCard,
  Edit,
  MapPin,
  Plus,
  ShoppingCart,
  Store,
  Trash2,
} from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Spinner } from "~/components/ui/spinner";
import type {
  StripeTerminalLocation,
  StripeTerminalReader,
} from "~/lib/stripe-terminal";

// Helper to determine reader status badge variant
const getReaderStatusBadgeVariant = (
  status: string
): "default" | "secondary" | "outline" => {
  if (status === "online") {
    return "default";
  }
  if (status === "offline") {
    return "secondary";
  }
  return "outline";
};

interface LocationTerminalCardProps {
  location: {
    id: Id<"locations">;
    name: string;
    stripeTerminalLocationId?: string;
  };
  locationInfo?: StripeTerminalLocation;
  readers: StripeTerminalReader[];
  isLoadingReaders: boolean;
  onOrder: (locationId: Id<"locations">) => void;
  onRegister: (locationId: string) => void;
  onEditReader: (readerId: string, label?: string) => void;
  onDeleteReader: (
    readerId: string,
    label?: string,
    deviceType?: string
  ) => void;
}

export function LocationTerminalCard({
  location,
  locationInfo,
  readers,
  isLoadingReaders,
  onOrder,
  onRegister,
  onEditReader,
  onDeleteReader,
}: LocationTerminalCardProps) {
  const hasTerminalLocation = Boolean(location.stripeTerminalLocationId);
  const isLoadingTerminalLocationInfo =
    hasTerminalLocation && locationInfo === undefined;
  const canRegisterReader = Boolean(location.stripeTerminalLocationId);

  let locationDescription: React.ReactNode = (
    <span className="text-warning">
      Stripe Terminal Location not configured
    </span>
  );

  if (locationInfo) {
    locationDescription = (
      <span className="flex items-center gap-1">
        <MapPin className="size-3" />
        {locationInfo.address.line1}, {locationInfo.address.city},{" "}
        {locationInfo.address.state}
      </span>
    );
  } else if (isLoadingTerminalLocationInfo) {
    locationDescription = (
      <span className="text-warning">Loading Stripe Terminal Location...</span>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="bg-primary/10 flex size-12 items-center justify-center rounded-lg">
            <Store className="text-primary size-6" />
          </div>
          <div>
            <CardTitle className="flex items-center gap-2">
              {location.name}
            </CardTitle>
            <CardDescription className="mt-1">
              {locationDescription}
            </CardDescription>
            <p className="caption text-muted-foreground mt-1">
              Stripe Terminal Location ID:{" "}
              {location.stripeTerminalLocationId ?? "Not configured"}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => onOrder(location.id)}
            size="sm"
            variant="outline"
          >
            <ShoppingCart className="mr-2 size-4" />
            Order Card Reader
          </Button>
          <Button
            disabled={!canRegisterReader}
            onClick={() => {
              if (!location.stripeTerminalLocationId) {
                return;
              }

              onRegister(location.stripeTerminalLocationId);
            }}
            size="sm"
            variant="outline"
          >
            <Plus className="mr-2 size-4" />
            Register S700
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        {isLoadingReaders && (
          <div className="flex items-center justify-center py-6">
            <Spinner className="size-5" />
          </div>
        )}
        {!isLoadingReaders && readers.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <CreditCard className="text-muted-foreground/30 mb-3 size-10" />
            <p className="subtitle-sm text-foreground mb-1">
              No Card Readers Yet
            </p>
            <p className="body-sm text-muted-foreground max-w-sm">
              Order a card reader to accept in-person payments. S700 readers can
              be registered here once a Stripe Terminal Location is configured.
              M2 readers are set up in the POS app.
            </p>
            <div className="mt-4 flex gap-3">
              <Button
                onClick={() => onOrder(location.id)}
                size="sm"
                variant="default"
              >
                <ShoppingCart className="mr-2 size-4" />
                Order Card Reader
              </Button>
              <Button
                disabled={!canRegisterReader}
                onClick={() => {
                  if (!location.stripeTerminalLocationId) {
                    return;
                  }

                  onRegister(location.stripeTerminalLocationId);
                }}
                size="sm"
                variant="outline"
              >
                <Plus className="mr-2 size-4" />
                Register S700
              </Button>
            </div>
          </div>
        )}
        {!isLoadingReaders && readers.length > 0 && (
          <div className="divide-y">
            {readers.map((reader) => (
              <div
                className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                key={reader.id}
              >
                <div className="flex items-center gap-3">
                  <div className="bg-secondary flex size-10 items-center justify-center rounded-lg">
                    <CreditCard className="text-muted-foreground size-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="subtitle-sm">
                        {reader.label ?? reader.deviceType}
                      </h4>
                      <Badge
                        variant={getReaderStatusBadgeVariant(reader.status)}
                      >
                        {reader.status}
                      </Badge>
                    </div>
                    <p className="body-sm text-muted-foreground">
                      {reader.deviceType} • S/N: {reader.serialNumber}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => onEditReader(reader.id, reader.label)}
                    size="sm"
                    variant="outline"
                  >
                    <Edit className="size-4" />
                  </Button>
                  <Button
                    onClick={() =>
                      onDeleteReader(reader.id, reader.label, reader.deviceType)
                    }
                    size="sm"
                    variant="outline"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
